import { forwardRef, useEffect } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, FormControlLabel, Switch, Typography } from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import Button from '@/trio-ui/Button';
import InputText from '@/trio-ui/InputText';
import TransparentButton from '@/trio-ui/TransparentButton';
import { AvailableRoles } from '@/types';
import { User } from '@/types/api';
import { invalidEmailMessage, requiredMessage } from '@/utils/formsValidation';
import * as Styles from './NewUserFormModal.styles';
import RoleSelector from '../RoleSelector';

export const userFormSchema = yup.object({
  name: yup.string(),
  email: yup.string().email(invalidEmailMessage).required(requiredMessage),
  roles: yup
    .array()
    .of(yup.mixed<AvailableRoles>().oneOf(Object.values(AvailableRoles)))
    .min(1, requiredMessage),
  usePassword: yup.boolean(),
  password: yup.string().when('usePassword', {
    is: true,
    then: (schema) => schema.required(requiredMessage).min(6, 'Minimum 6 characters'),
  }),
});

export type FormData = {
  name?: string;
  email: string;
  roles: AvailableRoles[];
  usePassword?: boolean;
  password?: string;
};

export type NewUserFormModalComponentProps = {
  isOpen?: boolean;
  onClose?: () => void;
  onSubmit?: (data: FormData) => void;
  isLoading?: boolean;
  editing?: User;
  onDeleteClick?: (userId: string) => void;
};

const NewUserFormModal = forwardRef<HTMLDivElement, NewUserFormModalComponentProps>(
  (
    { isOpen = false, onClose, onSubmit, isLoading = false, editing, onDeleteClick },
    ref
  ): JSX.Element => {
    const { handleSubmit, values, handleChange, errors, touched, resetForm, setFieldValue } =
      useFormik<FormData>({
        initialValues: {
          email: '',
          roles: [],
          usePassword: false,
          password: '',
        },
        validateOnBlur: false,
        validationSchema: userFormSchema,
        onSubmit: (data) => {
          if (isLoading) return;

          const formData = {
            email: data.email || '',
            roles: (data.roles as AvailableRoles[]) || [],
            usePassword: data.usePassword,
            password: data.usePassword ? data.password : undefined,
          } satisfies FormData;

          onSubmit?.(formData);
        },
      });

    useEffect(() => {
      if (isOpen) {
        resetForm();
        setFieldValue('email', editing?.email || '');
        setFieldValue(
          'roles',
          editing?.roles.map((role) => role?.value).filter((value) => !!value) || []
        );
      }
    }, [isOpen, setFieldValue, editing, resetForm]);

    const [parsedRolesErrorMessage] = Array.isArray(errors.roles) ? errors.roles : [errors.roles];
    const rolesErrorMessage = touched.roles ? parsedRolesErrorMessage || '' : '';

    return (
      <Styles.Drawer $isOpen={isOpen} ref={ref}>
        <Styles.Header>
          <Styles.HeaderTitle>{editing ? editing.name : 'Invite User'}</Styles.HeaderTitle>
        </Styles.Header>

        <Styles.Scrollable onSubmit={handleSubmit}>
          <InputText
            isLabelAboveField
            label="Email"
            name="email"
            placeholder="user@company.com"
            value={values.email}
            onChange={handleChange}
            errorMessage={(touched.email && errors.email) || undefined}
          />

          <RoleSelector
            name="roles"
            label="Roles"
            isLabelAboveField
            value={values.roles as AvailableRoles[]}
            onChange={handleChange}
            errorMessage={rolesErrorMessage}
          />

          {!editing && (
            <Box sx={{ mt: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={values.usePassword}
                    onChange={(e) => {
                      const { checked } = e.target;
                      setFieldValue('usePassword', checked);
                      if (checked) {
                        const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
                        const generated = Array.from({ length: 10 }, () =>
                          chars.charAt(Math.floor(Math.random() * chars.length))
                        ).join('');
                        setFieldValue('password', generated);
                      } else {
                        setFieldValue('password', '');
                      }
                    }}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2" fontWeight={500}>
                    Set temporary password
                  </Typography>
                }
              />
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: 'block', mt: 0.5 }}
              >
                {values.usePassword
                  ? 'User will be asked to reset their password on first login.'
                  : 'An invite email will be sent to the user.'}
              </Typography>

              {values.usePassword && (
                <Box sx={{ mt: 1.5 }}>
                  <InputText
                    isLabelAboveField
                    label="Temporary Password"
                    name="password"
                    type="text"
                    placeholder="Min. 6 characters"
                    value={values.password}
                    onChange={handleChange}
                    errorMessage={(touched.password && errors.password) || undefined}
                  />
                </Box>
              )}
            </Box>
          )}

          {editing && (
            <div className="tw-border-t tw-pt-2.5 tw-border-neutral-40">
              <TransparentButton
                isRedButton
                icon={DeleteIcon}
                type="button"
                onClick={() => onDeleteClick?.(editing.id)}
              >
                Delete User
              </TransparentButton>
            </div>
          )}

          <Styles.Footer>
            <Button type="submit" disabled={isLoading}>
              Save
            </Button>
            <Button type="button" variantStyle="outline" onClick={onClose}>
              Cancel
            </Button>
          </Styles.Footer>
        </Styles.Scrollable>
      </Styles.Drawer>
    );
  }
);

export default NewUserFormModal;
